import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'ghost' | 'outline' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'prefix'> {
  variant?: Variant;
  size?: Size;
  /** Visual leading decoration (icon, emoji). */
  leading?: ReactNode;
  /** Visual trailing decoration (chevron, badge). */
  trailing?: ReactNode;
  /** Make the button full-width. */
  block?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-accent-600 text-white hover:bg-accent-700 shadow-sm',
  ghost:
    'bg-transparent text-ink-700 hover:bg-ink-100 dark:text-ink-100 dark:hover:bg-ink-700',
  outline:
    'border border-ink-200 bg-white text-ink-900 hover:bg-ink-50 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50 dark:hover:bg-ink-700',
  danger: 'bg-neg-600 text-white hover:bg-neg-700 shadow-sm',
  subtle:
    'bg-ink-100 text-ink-800 hover:bg-ink-200 dark:bg-ink-700 dark:text-ink-100 dark:hover:bg-ink-600',
};

const SIZE: Record<Size, string> = {
  sm: 'rounded-lg px-2.5 py-1.5 text-xs gap-1.5',
  md: 'rounded-xl px-4 py-2.5 text-sm gap-2',
  lg: 'rounded-xl px-5 py-3 text-base gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', leading, trailing, block, className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      {...rest}
      className={`inline-flex items-center justify-center font-semibold transition active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none ${VARIANT[variant]} ${SIZE[size]} ${block ? 'w-full' : ''} ${className}`}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
});
